/**
 * KeyLocationInput — captura la Ubicación Clave (destino frecuente) del usuario.
 *
 * Flow:
 *   1. Usuario escribe la referencia (dirección o punto conocido).
 *   2. En blur / Enter, llama a POST /api/advisor/key-location/resolve.
 *   3. Muestra el resultado geocoded o un mensaje de "no logré ubicarla".
 *   4. En "Continuar" pasa el objeto resuelto al padre.
 *
 * El input mantiene la voz del Advisor — sin fricción, con posibilidad
 * de omitir (BR-005: el usuario controla su contexto).
 */
import React, { useState } from 'react';
import { MapPin, Loader2, Check, ArrowRight } from 'lucide-react';
import { api } from '../../lib/api';

export default function KeyLocationInput({
  placeholder = 'Ej: Calle 100 con 15, Bogotá',
  onSubmit,
  onSkip,
  disabled,
  city,
}) {
  const [text, setText] = useState('');
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const attemptResolve = async () => {
    const q = text.trim();
    if (!q) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.resolveKeyLocation({ query: q, city });
      setResolved(res);
    } catch (err) {
      setError(err?.message || 'No pudimos ubicarla. Puedes continuar sin coordenadas.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (resolved) {
      onSubmit({
        label: resolved.label,
        short_label: resolved.short_label,
        lat: resolved.lat,
        lng: resolved.lng,
      });
      return;
    }
    if (text.trim()) {
      // Enviar sólo label si no se resolvió (permite continuar sin bloquear)
      onSubmit({ label: text.trim(), short_label: text.trim().slice(0, 40) });
    }
  };

  return (
    <div className="space-y-4" data-testid="key-location-input">
      <div className="relative">
        <MapPin className="w-5 h-5 text-habita absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={text}
          onChange={(e) => { setText(e.target.value); setResolved(null); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!resolved) attemptResolve();
              else handleContinue();
            }
          }}
          onBlur={() => { if (text.trim() && !resolved && !loading) attemptResolve(); }}
          placeholder={placeholder}
          disabled={disabled || loading}
          data-testid="key-location-text"
          className="w-full h-14 pl-12 pr-4 rounded-2xl border-2 border-gray-200 focus:border-habita focus:outline-none text-base"
        />
      </div>

      {loading && (
        <p className="text-sm text-gray-500 flex items-center gap-2" data-testid="key-location-loading">
          <Loader2 className="w-4 h-4 animate-spin text-habita" />
          Buscando esa ubicación...
        </p>
      )}

      {resolved?.matched && (
        <div
          className="flex items-start gap-2 p-3 rounded-xl bg-habita-mint/50 border border-habita/20"
          data-testid="key-location-resolved"
        >
          <Check className="w-4 h-4 text-habita mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-habita-dark">{resolved.short_label}</p>
            <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{resolved.label}</p>
          </div>
        </div>
      )}

      {resolved && !resolved.matched && (
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="key-location-fallback">
          <p className="text-xs text-amber-800">
            No logré ubicarla con exactitud. Puedo guardarla como referencia y aún así darte recomendaciones — sólo no calcularé tiempos de trayecto.
          </p>
        </div>
      )}

      {error && (
        <p className="text-sm text-rose-600" data-testid="key-location-error">{error}</p>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleContinue}
          disabled={disabled || !text.trim()}
          data-testid="key-location-continue"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-habita text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          Continuar <ArrowRight className="w-4 h-4" />
        </button>
        {onSkip && (
          <button
            onClick={onSkip}
            disabled={disabled}
            data-testid="key-location-skip"
            className="text-sm text-gray-500 hover:text-habita underline underline-offset-4"
          >
            Prefiero no decirlo por ahora
          </button>
        )}
      </div>
    </div>
  );
}
